<script setup lang="ts">
/* eslint-disable tailwindcss/no-custom-classname */
import { isNumber } from 'lodash-es';
import {
  computed,
  onMounted,
  onUnmounted,
  ref,
  useTemplateRef,
} from 'vue';

import VIconButton from '@components/buttons/VIconButton/VIconButton.vue';
import {
  computedWithControl,
  useMutationObserver,
  useScroll,
  useWindowSize,
  watchDebounced,
  watchImmediate,
} from '@vueuse/core';

export interface IVScrollProps {
  elementClass: string;
  activeIndex: number | undefined;
  disableShadowTransition?: boolean;
  fullBorderWidth?: boolean;
}

const props = defineProps<IVScrollProps>();

const { width: windowWidth } = useWindowSize();
const scrollContainer = useTemplateRef<HTMLElement>('scrollContainer');
const { x: scrollX, arrivedState, measure } = useScroll(scrollContainer, { behavior: 'smooth' });
const scrollItems = ref<HTMLElement[]>();
const maxScroll = ref<number>(0);
const scrollBorderOffset = 50; // controls remaining scroll that would be included into scrollStep to prevent tiny scroll left
let stop = () => {};

const shadowCommonClasses = computed(() => (
  `opacity-1 pointer-events-none absolute top-0 z-10 h-[calc(100%+2px)] w-6 ${props.disableShadowTransition ? '' : 'transition-shadow'}`
));

const showButtons = computedWithControl(() => [windowWidth.value, scrollContainer.value], () => {
  const scrollWidth = scrollContainer.value?.scrollWidth;
  const containerWidth = scrollContainer.value?.clientWidth;
  return scrollWidth && containerWidth && scrollWidth !== containerWidth;
});

function scrollIntoView(index: number = props.activeIndex || 0): void {
  if (scrollContainer.value && scrollItems.value?.length) {
    const container: HTMLElement = scrollContainer.value;
    const targetItem = scrollItems.value[index];

    if (targetItem) {
      scrollX.value = targetItem.offsetLeft - container.getBoundingClientRect().width / 2 + targetItem.getBoundingClientRect().width / 2;
    }
  }
}

function updateDimensions(): void {
  const container = scrollContainer.value;
  if (container) {
    maxScroll.value = container.scrollWidth - container.offsetWidth;
    measure();
    scrollIntoView();
  }
}

function init(): void {
  const container = scrollContainer.value;
  if (container) {
    const itemsNodeList = container.querySelectorAll<HTMLElement>(`.${props.elementClass}`);
    if (itemsNodeList) {
      scrollItems.value = Array.from(itemsNodeList);
      updateDimensions();
    }
  }
}

const scrollStep = computed(() => (scrollContainer.value ? scrollContainer.value.getBoundingClientRect().width / 2 : 0));

function scrollBack(): void {
  const result = scrollX.value - scrollStep.value;
  scrollX.value = result < scrollBorderOffset ? 0 : result;
}

function scrollForward(): void {
  const result = scrollX.value + scrollStep.value;
  if (maxScroll.value) {
    scrollX.value = result > maxScroll.value - scrollBorderOffset ? maxScroll.value : result;
  }
}

watchDebounced([windowWidth, showButtons], () => updateDimensions(), { debounce: 100 });

onMounted(() => {
  init();
  const observer = useMutationObserver(scrollContainer, () => {
    init();
  }, {
    childList: true,
    subtree: true,
  });
  stop = observer.stop;
});

onUnmounted(() => {
  stop();
});

watchImmediate(() => props.activeIndex, (newValue) => {
  if (isNumber(newValue)) {
    scrollIntoView(newValue);
  }
});
</script>

<template>
  <div v-data-qa-auto-id="'VHorizontalScrollNav'" class="flex items-center">
    <div
      v-if="showButtons"
      class="relative flex items-center self-stretch rounded-l border px-1"
    >
      <VIconButton
        :disabled="arrivedState.left"
        icon="pi-angle-left"
        @click="scrollBack"
      />
    </div>
    <div
      class="relative overflow-hidden"
      :class="{ 'w-full': fullBorderWidth }"
    >
      <div
        class="left-0 shadow-[0px_0_10px_0px_#9e9d9d] translate-3d-x-[-100%]"
        :class="[
          { '!shadow-none': arrivedState.left },
          shadowCommonClasses,
        ]"
      />
      <div
        class="right-0 shadow-[0px_0_10px_0px_#9e9d9d] translate-3d-x-[100%]"
        :class="[
          { '!shadow-none': arrivedState.right },
          shadowCommonClasses,
        ]"
      />
      <div
        ref="scrollContainer"
        class="no-scrollbar overflow-x-auto"
        :class="{ flex: !fullBorderWidth }"
      >
        <slot />
      </div>
    </div>

    <div
      v-if="showButtons"
      class="relative flex items-center self-stretch rounded-r border px-1"
    >
      <VIconButton
        :disabled="arrivedState.right"
        icon="pi-angle-right"
        @click="scrollForward"
      />
    </div>
  </div>
</template>
